/**
 * THE one resolver for the client's add-on menu.
 *
 * THE MENU IS LIVE, THE QUOTED PRICE IS FROZEN (Oct 2026):
 *   • WHAT IS OFFERED and everything that is NOT money — name, benefit text,
 *     category, photo, and the full current set of options — come from the
 *     LIVE catalogue (`catalog`), the items the owner has switched ON. A typo
 *     they fix, a colour they add, a photo they upload all reach every open
 *     proposal immediately.
 *   • WHAT IT COSTS is decided per item, and per option inside a group, by one
 *     rule against the frozen `snapshot`:
 *       - if that item/option is in the snapshot WITH a price → the client sees
 *         THE SNAPSHOT PRICE (they were quoted it; it is theirs);
 *       - otherwise → today's LIVE price (it never appeared on their proposal,
 *         so there is no earlier promise to keep);
 *       - if neither carries a price → it is DROPPED and counted in
 *         `droppedNoPriceIds`. Never render $0.
 *
 * Matching is ID-FIRST with the legacy name-slug fallback this file has always
 * used — items by their durable id, options by their own durable id. No new
 * matching is written here.
 *
 * NEVER WRITE TO THE SNAPSHOT. This function reads it for prices only. The
 * generation-time snapshot is the price record and nothing else; it is not
 * re-snapshotted, topped up or deleted anywhere in this file.
 *
 * WHY A FREEZE EXISTED AT ALL — the history this rule preserves:
 *   Generating a proposal froze the ENTIRE catalogue into
 *   proposal_contents.addOnsSnapshot, and the menu was rendered from that copy
 *   forever. The freeze protected ONE thing correctly — a client must never be
 *   re-quoted a price they were already shown — but it also froze names,
 *   benefits, categories, options and photos, so a priced colour the owner
 *   added after generation never reached the client (EVT-c93be65e: a flower
 *   wall stuck at one colourless option while the live catalogue had three at
 *   $500). Before that, visibility was decided in two places by two rules and a
 *   frozen `hidden` flag could override a live decision, so the client saw
 *   nothing while the admin was told two items were on offer. This resolver
 *   keeps both promises: visibility and the menu are LIVE, only the quoted
 *   price is frozen, and the snapshot's own `hidden` flag is never read.
 *
 * Identity note: the rendered `id` is the add-on's name slug. Two entries
 * sharing a name would collapse into ONE identity, so the FIRST occurrence
 * keeps the plain slug and only repeats get a numeric suffix.
 */

const moneyAmount = (x) => {
  if (!x) return null;                 // no row / no option → NO QUOTED PRICE
  if (x.amount != null && Number(x.amount) > 0) return Number(x.amount);
  return null;
};
const priceText = (x) => {
  if (!x) return null;                 // no row → NO QUOTED PRICE (never read .price off null)
  const t = String(x.price == null ? '' : x.price).trim();
  return (t && /\d/.test(t)) ? t : null;
};
// A quoted price exists only when a snapshot row/option is present AND carries
// a price. A missing row is a normal answer, not an object to dereference.
const quotedPrice = (snap) => {
  if (!snap) return null;              // missing snapshot row → no quoted price
  const amt = moneyAmount(snap);
  if (amt != null) return { amount: amt, price: priceText(snap) };
  const txt = priceText(snap);
  if (txt != null) return { amount: null, price: txt };
  return null;                         // present but priceless → no quoted price
};

export const addOnSlug = (value, index) =>
  String(value || `addon-${index}`).toLowerCase().replace(/[^a-z0-9]+/g, '-');

// Phase 1 — mint a durable, opaque add-on identifier on the client at
// creation time so it travels with the item from birth (the save path only
// preserves ids, it does not re-mint them, which is what keeps them stable
// across renames). The underscore prefix guarantees it can never collide with
// a runtime slug. Mirrors backend utils/addon_identity.mint_addon_id.
export const mintAddOnId = () => {
  const rand = (typeof crypto !== 'undefined' && crypto.randomUUID)
    ? crypto.randomUUID().replace(/-/g, '').slice(0, 16)
    : (Date.now().toString(16) + Math.random().toString(16).slice(2)).slice(0, 16);
  return `aoid_${rand}`;
};

// Resolve one item's money (and its options') against the matched snapshot row.
// Everything not-money stays on the live item; only amount/price/option amounts
// may be overridden by the frozen snapshot. Options with no price are dropped.
const resolveMoney = (liveItem, snapRow) => {
  // The one rule, per item and per option, stated so it cannot be misread:
  //   • snapshot row exists AND carries a price → snapshot (quoted) price
  //   • otherwise, live price carries            → live price
  //   • neither                                  → drop the entry, count it
  // `snapRow` (or a matched snapshot option) may legitimately be absent — an
  // item/option added to the live catalogue after this proposal was generated
  // has no snapshot row at all. That is a NORMAL answer, never dereferenced.
  const liveOpts = Array.isArray(liveItem.options) ? liveItem.options.filter((o) => o && o.name) : [];
  const isGroup = liveOpts.length > 0;
  if (isGroup) {
    const snapOpts = (snapRow && Array.isArray(snapRow.options)) ? snapRow.options.filter((o) => o && o.name) : [];
    const snapById = new Map();
    const snapBySlug = new Map();
    snapOpts.forEach((so, k) => {
      if (typeof so.id === 'string' && so.id.trim()) snapById.set(so.id, so);
      const s = addOnSlug(so.name, k);
      if (!snapBySlug.has(s)) snapBySlug.set(s, so);
    });
    const options = [];
    liveOpts.forEach((lo, k) => {
      // May be null (option not in the snapshot). Not-found is first-class.
      const so = (typeof lo.id === 'string' && lo.id.trim() && snapById.get(lo.id))
        || snapBySlug.get(addOnSlug(lo.name, k)) || null;
      const quoted = quotedPrice(so);            // null when no snapshot option / priceless
      const amount = quoted && quoted.amount != null ? quoted.amount : moneyAmount(lo); // else today's live price
      if (amount == null) return;                // neither → drop this option (never $0)
      options.push({ id: lo.id || null, name: lo.name, benefit: lo.benefit || '', photo: lo.photo || '', amount });
    });
    return { isGroup: true, options, amount: null, price: null, priced: options.length > 0 };
  }
  // Item's own price. quotedPrice() is null when the snapshot row is missing
  // OR present-but-priceless — both mean "fall through to the live price".
  const quoted = quotedPrice(snapRow);
  let amount;
  let price;
  if (quoted) {                                  // in snapshot WITH a price → frozen
    amount = quoted.amount;
    price = quoted.price;
  } else {                                       // else today's live price
    amount = moneyAmount(liveItem);
    price = priceText(liveItem);
  }
  return { isGroup: false, options: [], amount, price, priced: amount != null || price != null };
};

export function resolveProposalAddOns(catalog, snapshot, hiddenKeys, featuredKeys, shownKeys) {
  // `catalog` defines WHICH items exist and every not-money field. For the
  // client this is the LIVE, switched-ON catalogue. `snapshot` provides FROZEN
  // prices only and is never mutated.
  const snap = Array.isArray(snapshot) ? snapshot : [];
  const snapById = new Map();
  const snapBySlug = new Map();
  snap.forEach((s, i) => {
    if (!s) return;
    const sid = (typeof s.id === 'string' && s.id.trim()) ? s.id : null;
    if (sid) snapById.set(sid, s);
    const ss = addOnSlug(s.key || s.name || s.title, i);
    if (!snapBySlug.has(ss)) snapBySlug.set(ss, s);
  });

  const seen = new Map();
  const droppedNoPriceIds = [];
  const items = (Array.isArray(catalog) ? catalog : [])
    .filter((a) => a && (a.name || a.title))
    .map((a, i) => {
      const base = addOnSlug(a.key || a.name || a.title, i);
      const n = (seen.get(base) || 0) + 1;
      seen.set(base, n);
      const id = n === 1 ? base : `${base}-${n}`;
      const addOnId = (typeof a.id === 'string' && a.id.trim()) ? a.id : null;
      // Id-first, legacy name-slug fallback — the SAME matching this file has
      // always used, now to pull FROZEN PRICE from the snapshot.
      const snapRow = (addOnId && snapById.get(addOnId)) || snapBySlug.get(id) || snapBySlug.get(base) || null;
      const money = resolveMoney(a, snapRow);
      if (!money.priced) droppedNoPriceIds.push(addOnId || id);
      return {
        id,
        addOnId,
        // NOT-MONEY — live.
        name: a.name || a.title,
        benefit: ((snapRow && typeof snapRow.benefitOverride === 'string' && snapRow.benefitOverride.trim())
                    ? snapRow.benefitOverride
                    : (typeof a.benefitOverride === 'string' && a.benefitOverride.trim())
                      ? a.benefitOverride
                      : (a.benefit || a.description || '')),
        // MONEY — snapshot when quoted, else live.
        price: money.price,
        amount: money.amount,
        options: money.options,
        isGroup: money.isGroup,
        priced: money.priced,
        // The snapshot's own flag is reported, NEVER read for visibility.
        snapshotHidden: a.hidden === true,
        visible: money.priced,
        index: i,
        raw: a, // the original live catalogue item, untouched
      };
    });

  // ── PER-EVENT CURATION — the ONE place client visibility is decided ──
  // OPT-IN: `shownKeys` is the proposal's allow list. An add-on reaches the
  // client ONLY when its key (slug id OR durable addOnId) is in that list.
  // Absent/null/undefined counts as an EMPTY list — nothing is shown. There is
  // no "show everything when uncurated" branch; the admin opts each one in.
  // `hiddenKeys` still removes anything explicitly excluded. `featuredKeys`,
  // when an ARRAY, orders the shown set (and an empty array offers nothing).
  // Price-freeze (snapshot) is untouched by any of this.
  const hiddenSet = new Set((Array.isArray(hiddenKeys) ? hiddenKeys : []).map((k) => String(k)));
  const isHidden = (it) => hiddenSet.has(it.id) || (it.addOnId && hiddenSet.has(it.addOnId));
  const shownSet = new Set((Array.isArray(shownKeys) ? shownKeys : []).map((k) => String(k)));
  const isShownKey = (it) => shownSet.has(it.id) || (it.addOnId && shownSet.has(it.addOnId));
  const hasFeatured = Array.isArray(featuredKeys);
  const featuredList = hasFeatured ? featuredKeys.map((k) => String(k)) : [];

  items.forEach((it) => {
    // SHOWN = curated in, not excluded. Price is NOT part of this answer: the
    // client store renders a shown add-on even when it has no price yet (it
    // simply omits the price line). `visible` keeps the stricter, priced rule
    // the contract/selection paths rely on.
    it.shown = !isHidden(it) && isShownKey(it);
    it.visible = it.priced && it.shown;
  });
  let visible = items.filter((it) => it.visible);

  if (hasFeatured) {
    const byKey = new Map();
    visible.forEach((it) => { byKey.set(it.id, it); if (it.addOnId) byKey.set(it.addOnId, it); });
    const ordered = [];
    const pushed = new Set();
    featuredList.forEach((k) => {
      const it = byKey.get(k);
      if (it && !pushed.has(it.id)) { ordered.push(it); pushed.add(it.id); }
    });
    visible = ordered;
    const keepIds = new Set(visible.map((it) => it.id));
    items.forEach((it) => { it.visible = keepIds.has(it.id); });
  }

  return {
    items,
    visible,
    visibleIds: visible.map((it) => it.id),
    allIds: items.map((it) => it.id),
    visibleCount: visible.length,
    hiddenCount: items.length - visible.length,
    total: items.length,
    offeredIds: visible.map((it) => it.id),
    offeredCount: visible.length,
    featuredApplied: hasFeatured,
    droppedNoPriceIds,
    droppedNoPriceCount: droppedNoPriceIds.length,
  };
}

export default resolveProposalAddOns;
